import React, { useCallback } from 'react';

import { Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { isEqual } from 'lodash';

import {
  ActionsRequisesDtoActionsRequisesEnum,
  TraitementResponseDtoListStatutEnum,
} from 'lib_api/lib/api/gen';

import { FilterButton } from 'components/WrappedComponents';
import DashboardSearchBarWithTooltip from 'components/WrappedComponents/SearchBar/DashboardSearchBarWithTooltip';
import { useJusticeFilterContext } from 'hooks/JusticeFilterStoreContext';

import {
  DashboardJusticeFilter,
  DashboardJusticeFilterForm,
} from '../../types';

import FilterFormJustice from './FilterFormJustice/FilterFormJustice';

interface FiltersProps {
  filter: Partial<DashboardJusticeFilter>;
  setFilter: (value: Partial<DashboardJusticeFilter>) => void;
  actionsRequises?: ActionsRequisesDtoActionsRequisesEnum[];
}

function keepDossierSortis(
  statut: TraitementResponseDtoListStatutEnum[],
  sortieFilter: boolean,
  motifDeSortieFilter: string[],
): boolean {
  // automatically check the "sorti" checkbox
  return (
    // if the statut SORTI is selected
    statut.includes(TraitementResponseDtoListStatutEnum.SORTI) ||
    // if the checkbox is checked
    (statut.length === 0 && sortieFilter) ||
    motifDeSortieFilter.length !== 0
  );
}

function Filters({
  filter,
  setFilter,
  actionsRequises,
}: Readonly<FiltersProps>): React.ReactElement {
  const {
    includeDossiersSortis,
    includeOnlyActionsRequises,
    search,
    ...formFilter
  } = filter;

  const [, , justiceActionsRequises] = useJusticeFilterContext();

  const keepOnlyActionsRequises = useCallback(
    (actionsRequises?: ActionsRequisesDtoActionsRequisesEnum[]): boolean => {
      return isEqual(new Set(actionsRequises), new Set(justiceActionsRequises));
    },
    [justiceActionsRequises],
  );

  function setFormFilter(value: Partial<DashboardJusticeFilterForm>) {
    const { statut, actionsRequises, motifDeSortie } = value;
    setFilter({
      ...filter,
      ...value,
      includeDossiersSortis: keepDossierSortis(
        statut ?? [],
        filter.includeDossiersSortis ?? false,
        motifDeSortie ?? [],
      ),
      includeOnlyActionsRequises: keepOnlyActionsRequises(actionsRequises),
    });
  }

  function setIncludeDossiersSortis(event: CheckboxChangeEvent) {
    const { statut, motifDeSortie } = filter;
    const updatedStatut =
      statut?.filter(s => s !== TraitementResponseDtoListStatutEnum.SORTI) ||
      [];

    // Only add the SORTI statut filter if the statut filter is not empty
    if (event.target.checked && (statut?.length ?? -1) > 0) {
      updatedStatut.push(TraitementResponseDtoListStatutEnum.SORTI);
    }

    setFilter({
      ...filter,
      statut: updatedStatut,
      includeDossiersSortis: event.target.checked,
      motifDeSortie: event.target.checked ? motifDeSortie : [],
    });
  }

  function setIncludeOnlyActionsRequises(event: CheckboxChangeEvent) {
    const isChecked = event.target.checked;
    setFilter({
      ...filter,
      actionsRequises: isChecked ? justiceActionsRequises : undefined,
      includeOnlyActionsRequises: isChecked,
    });
  }

  return (
    <div className="dashboard-header-filter">
      <DashboardSearchBarWithTooltip
        search={search}
        filter={filter}
        setFilter={setFilter}
      />
      <FilterButton
        filter={formFilter}
        setFilter={setFormFilter}
        textInactiveFilter="Filtres"
        textActiveFilter="Filtres (actifs)"
      >
        {(setData, data) => (
          <FilterFormJustice
            filter={data}
            setFilter={setData}
            actionsRequises={actionsRequises}
          />
        )}
      </FilterButton>
      <Checkbox
        checked={includeDossiersSortis}
        onChange={setIncludeDossiersSortis}
      >
        Afficher les véhicules sortis
      </Checkbox>
      {justiceActionsRequises && justiceActionsRequises.length > 0 && (
        <Checkbox
          checked={includeOnlyActionsRequises}
          onChange={setIncludeOnlyActionsRequises}
        >
          Afficher uniquement les actions requises
        </Checkbox>
      )}
    </div>
  );
}

export default Filters;
